import { MyProjects, Users } from '@platform/client';
import { Icon, IconProps, NumericBadge } from '@platform/components';
import { ProfessionalType } from 'lib/client/src/platformapi.schemas';
import Skeleton from 'react-loading-skeleton';
import EVENTS from '../analytics/events';
import { ORG_DEFAULT_FAQ_LINK, ORG_TSH_FAQ_LINK } from '../constants/dashboard';
import dataTestids from '../constants/test-ids';
import { ROUTES } from '../routes/consts';
import FlexibleLink from '../util/FlexibleLink';

/* Lookup tables for the links to show for Professionals based on type of Professional
 * These are created as lookup tables due to historic need to special-case among the wide number of
 * pro types. Org reps by contrast use the same default links much more often & there are fewer cases, so those
 * variants are handled in the function body of the LeftNavPanel component. */
const PROFESSIONAL_DEFAULT_RESOURCES_LINK = '/professional-learning-resources';
const PROFESSIONAL_DEFAULT_FAQ_LINK =
  'https://zsupport.movingworlds.org/portal/en/kb/movingworlds-faqs/professional-faqs';

const DEFAULT_HELP_LINK = 'mailto:support@movingworlds.org';

const DEFAULT_EVENTS_LINK = `${ROUTES.COMMUNITY}${ROUTES.EVENTS}`;

const professionalEventsLinks: Record<ProfessionalType, string | null> = {
  ey: DEFAULT_EVENTS_LINK,
  f5: null,
  generic: DEFAULT_EVENTS_LINK,
  kering: DEFAULT_EVENTS_LINK,
  mwi: null,
  paypal: DEFAULT_EVENTS_LINK,
  reckitt: DEFAULT_EVENTS_LINK,
  sap: DEFAULT_EVENTS_LINK,
  tsh: DEFAULT_EVENTS_LINK,
  wmp: DEFAULT_EVENTS_LINK,
  lumen: DEFAULT_EVENTS_LINK,
};

const professionalResourcesLinks: Record<ProfessionalType, string | null> = {
  ey: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  f5: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  generic: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  kering: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  mwi: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  paypal: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  reckitt: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  sap: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  tsh: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  lumen: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  wmp: null,
};

const professionalFaqLinks: Record<ProfessionalType, string | null> = {
  ey: 'https://movingworlds.org/partner/ey/ey-coaches',
  f5: PROFESSIONAL_DEFAULT_FAQ_LINK,
  generic: PROFESSIONAL_DEFAULT_FAQ_LINK,
  kering: PROFESSIONAL_DEFAULT_FAQ_LINK,
  mwi: PROFESSIONAL_DEFAULT_FAQ_LINK,
  paypal:
    'https://support.movingworlds.org/hc/en-us/categories/360005828313-PayPal-Gives-Nonprofit-Experience-Program',
  reckitt: PROFESSIONAL_DEFAULT_FAQ_LINK,
  sap: 'https://movingworlds.org/partner/sap/acceleration-collective-employees#frequently-asked-questions',
  tsh: 'https://zsupport.movingworlds.org/portal/en/kb/movingworlds-faqs/professional-faqs/transform-support-hub-faqs-for-professionals',
  wmp: PROFESSIONAL_DEFAULT_FAQ_LINK,
  lumen: PROFESSIONAL_DEFAULT_FAQ_LINK,
};

/** An individual clickable link in the LeftNavPanel */
const NavItem = ({
  title,
  href,
  iconType,
  linkType,
  eventName,
  newTab,
}: {
  title: React.ReactNode;
  href: string;
  iconType: IconProps['type'];
  /** The Mixpanel event fired when the link is clicked */
  eventName: string;
  linkType?: Parameters<typeof FlexibleLink>[0]['type'];
  newTab?: boolean;
}) => {
  return (
    <div>
      <FlexibleLink href={href} type={linkType ?? 'internal'} eventName={eventName} newTab={newTab}>
        <div
          key={href}
          className='px-2 py-3 md:px-6 md:py-4 flex items-center group cursor-pointer'
        >
          <span className='hidden md:block pr-5 group-hover:text-brand-500'>
            <Icon type={iconType} size='24' />
          </span>
          <span className='underline-offset-4 decoration-brand-500 decoration-2 group-hover:underline'>
            {title}
          </span>
        </div>
      </FlexibleLink>
    </div>
  );
};

/** The vertical list of clickable links on the left hand side of each Dashboard page */
const LeftNavPanel = () => {
  const { isSuccess, data: user } = Users.useGetCurrentUser();
  const { data: count } = MyProjects.useCountUserActionItems();

  /** Bools to determine which side items are shown */
  const showCreateProject = user?.accountType === 'organization';
  const showFindExperteers = user?.accountType === 'organization';
  const showFindProjects = isSuccess && user?.accountType === 'experteer';
  const showBrowseOrganizations =
    isSuccess && user?.accountType === 'experteer' && user?.professionalType !== 'ey';

  let showEvents = false;

  if (isSuccess && user?.accountType === 'experteer') {
    showEvents = true;
  } else if (
    user?.accountType === 'organization' &&
    user.organization?.applicationStatus === 'approved'
  ) {
    showEvents = true;
  }

  // From apps/legacy/moirai/lib/entities/Representative.ts
  const showScaleUpToolkit =
    user?.accountType === 'organization' && user?.organization?.applicationStatus === 'approved';

  /** Links calculated for the side items displayed above which don't have an analagous lookup table or fixed
   * url */

  let eventsLink = '';

  if (isSuccess && user.accountType === 'experteer') {
    eventsLink = professionalEventsLinks[user?.professionalType] ?? '';
  } else if (isSuccess && user?.accountType === 'organization') {
    eventsLink = DEFAULT_EVENTS_LINK;
  }

  // TODO: This currently always shows for all users AFAICT. But, THIS SEEMS TO ALWAYS LEAD TO A /transform PAGE,
  // EVEN FOR F5! Need to confirm expected behavior w/ Kelsey
  // TODO: use this url for org: /transform/resources, _blank
  let resourcesLink: string | null = '';

  if (isSuccess && user.accountType === 'experteer') {
    resourcesLink = professionalResourcesLinks[user?.professionalType] ?? '';
  } else if (isSuccess && user?.accountType === 'organization') {
    resourcesLink = null; // We no longer show this for org reps
  }

  let faqLink = '';

  if (isSuccess && user.accountType === 'experteer') {
    faqLink = professionalFaqLinks[user?.professionalType] ?? '';
  } else if (isSuccess && user?.accountType === 'organization') {
    if (user?.organization?.applicationStatus === 'approved') {
      faqLink = ORG_TSH_FAQ_LINK;
    } else {
      faqLink = ORG_DEFAULT_FAQ_LINK;
    }
  }

  return (
    <div className='mb-2 md:min-w-[359px] md:px-4 lg:pl-10 text-nowrap flex flex-col gap-4'>
      {!isSuccess && (
        <div className='bg-white shadow rounded-lg divide-y divide-solid divide-gray-100'>
          <div className=''>
            {[1, 2, 3, 4, 5].map((i) => (
              <div className='p-6' key={`skeleton-${i}`}>
                <Skeleton count={1} />
              </div>
            ))}
          </div>
        </div>
      )}

      {isSuccess && (
        <div
          className='bg-white md:shadow md:rounded-lg md:divide-y divide-solid divide-gray-100'
          role='navigation'
          data-testid={dataTestids.DASHBOARD.LEFTNAV}
        >
          <NavItem
            title='Home'
            href={'/'}
            iconType='home-smile'
            linkType='internal'
            eventName={EVENTS.DASHBOARD.LEFT_NAV.DASHBOARD_CLICKED}
          />
          <NavItem
            title={
              <span>
                My Projects{' '}
                {count && count > 0 ?
                  <NumericBadge color='error' size='sm' number={count} />
                : ''}
              </span>
            }
            href={ROUTES.MY_PROJECTS}
            iconType='folder-check'
            linkType='internal'
            eventName={EVENTS.DASHBOARD.LEFT_NAV.MY_PROJECTS_CLICKED}
          />
          {showCreateProject && (
            <NavItem
              title='Create Project'
              href={'~/platform' + ROUTES.CREATE_PROJECT}
              iconType='file-plus-02'
              linkType='internal'
              eventName={EVENTS.DASHBOARD.LEFT_NAV.CREATE_PROJECT_CLICKED}
            />
          )}
          {showFindExperteers && (
            <NavItem
              title='Find Experteers'
              href='/platform/user-search'
              iconType='search-sm'
              linkType='internal'
              eventName={EVENTS.DASHBOARD.LEFT_NAV.FIND_PROFESSIONALS_CLICKED}
              newTab
            />
          )}
          {showFindProjects && (
            <NavItem
              title='Find Projects'
              href='/platform/project-search'
              iconType='search-sm'
              linkType='internal'
              newTab
              eventName={EVENTS.DASHBOARD.LEFT_NAV.FIND_PROJECTS_CLICKED}
            />
          )}

          <NavItem
            title='Community'
            href='/community'
            iconType='user-01'
            linkType='internal'
            eventName={EVENTS.DASHBOARD.LEFT_NAV.COMMUNITY_CLICKED}
          />
          {showBrowseOrganizations && (
            <NavItem
              title='Browse Organizations'
              href='/platform/project-search'
              iconType='building-04'
              linkType='internal'
              newTab
              eventName={EVENTS.DASHBOARD.LEFT_NAV.BROWSE_ORGANIZATIONS_CLICKED}
            />
          )}
          {showEvents && (
            <NavItem
              title='Events'
              href={eventsLink}
              iconType='calendar-check-01'
              linkType='internal'
              eventName={EVENTS.DASHBOARD.LEFT_NAV.EVENTS_CLICKED}
            />
          )}
          {showScaleUpToolkit && (
            <NavItem
              title='Scale-Up Toolkit'
              href='/dashboard/organization/scale-up'
              iconType='line-chart-up-02'
              linkType='legacy'
              newTab
              eventName={EVENTS.DASHBOARD.LEFT_NAV.SCALE_UP_TOOLKIT_CLICKED}
            />
          )}
          {resourcesLink && (
            <NavItem
              title='Resources'
              href={resourcesLink}
              iconType='book-open-01'
              linkType='legacy'
              eventName={EVENTS.DASHBOARD.LEFT_NAV.RESOURCES_CLICKED}
              newTab
            />
          )}
          {faqLink && (
            <NavItem
              title='Frequently asked questions'
              href={faqLink}
              iconType='list'
              linkType='external'
              eventName={EVENTS.DASHBOARD.LEFT_NAV.FAQ_CLICKED}
            />
          )}
          <NavItem
            title='Ask us a question'
            href={DEFAULT_HELP_LINK}
            iconType='annotation-question'
            linkType='external'
            eventName={EVENTS.DASHBOARD.LEFT_NAV.ASK_US_A_QUESTION_CLICKED}
          />
        </div>
      )}
      {/*  Disabled in initial community release until form is complete
      <Button
        variant='secondary-color'
        size='md'
        target='_blank'
        href='https://forms.zoho.com/movingworlds/form/FeedbackForm'
      >
        Give us platform feedback <Icon stroke='brand-500' type='star-06' size='16' />
      </Button> */}
    </div>
  );
};

export default LeftNavPanel;
